import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type {
  ObservationPhaseVocabulary,
  ReportPhoto,
  ReportPhotoGroup,
} from "@bitacorapro/api-types";
import type { PresentationalKind } from "../lib/postventa/phase";
import {
  groupPill,
  groupTitle,
  groupUnderPhotoReview,
  isProblemGroup,
  isUnattributedGroup,
  verdictFlag,
  type FlagTone,
  type GroupPill,
} from "../lib/photoGroups";
import { Icon } from "./icons";
import { Lightbox, type LightboxPhoto } from "./Lightbox";
import {
  AnnulledLine,
  ReviewNotice,
  ConfirmAnnulDialog,
  DECISION_TEXT,
  isPendingReview,
  PhotoReviewControls,
  remainingText,
  ReviewBadge,
  reviewBadge,
  reviewBadgeText,
  type AnnulRequest,
  type PhotoReviewContext,
  type ReviewBadgeModel,
} from "./PhotoReview";

// The DetalleTienda "Estado visual" gallery strip + lightbox (2026-08-09
// backend/frontend council, Piece A). Photos come from
// useVisitPhotos(reportId) — see apps/web-dashboard/src/lib/api/queries.ts
// for why this is a separate lazy query rather than embedded in the
// dashboard aggregate.
//
// Every field except `url` degrades honestly when the backend has no
// data for it (packages/api-types/src/report-photos.ts): omit the block,
// never fabricate a value. The one exception is the verdict flag — an
// absent badge reads as "conforme" on a quick scan, so a missing signal
// renders an explicit neutral ".vgal__flag--none" rather than no badge at
// all (four independent 2026-08-09 council findings converged on this:
// ux-expert, accessibility-expert, design-expert, test-engineer).
//
// ADR-0137: the badge reads `verdict` (the supervisor verdict of the
// checklist item the photo was attached to — tone and label come from
// the recipe server-side); `conformity` is the deprecated fallback for a
// core-api that predates `verdict`.

type Tone = "ok" | "low" | "med" | "high" | "none";

function conformityTone(c: ReportPhoto["conformity"]): Tone {
  if (c === "conforme") return "ok";
  if (c === "leve") return "low";
  if (c === "moderada") return "med";
  if (c === "critica") return "high";
  return "none";
}

function conformityLabel(c: ReportPhoto["conformity"]): string {
  if (c === "conforme") return "Conforme"; // fitness-allow: postventa-labels-single-source — facility, vocabulario propio de esa receta
  if (c === "leve") return "Leve"; // fitness-allow: postventa-labels-single-source — facility, vocabulario propio de esa receta
  if (c === "moderada") return "Moderada"; // fitness-allow: postventa-labels-single-source — facility, vocabulario propio de esa receta
  if (c === "critica") return "Crítica";
  return "Sin evaluar";
}

// Every photo carries a badge, in both variants (founder, 2026-10-12):
// the verdict when there is one; «No aplica» (neutral) for a photo of an
// item marked n/a; otherwise «Sin evaluar» (neutral) — an unreviewed item
// or a photo attributed to no item.
function photoFlag(photo: ReportPhoto): { tone: Tone; label: string } {
  if (photo.verdict) return { tone: photo.verdict.tone, label: photo.verdict.label };
  if (photo.itemConformance === "not_applicable") return { tone: "none", label: "No aplica" };
  return { tone: conformityTone(photo.conformity), label: conformityLabel(photo.conformity) };
}

type PhotoGalleryProps = {
  photos: ReportPhoto[];
  /** Opt-in (store detail only): one card per checklist item instead of
   *  one tile per photo. Empty → today's per-photo tiles, unchanged (an
   *  older core-api, or a report type whose state isn't checklist items);
   *  photos are never re-grouped client-side by label or id. */
  groups?: readonly ReportPhotoGroup[];
  /** The recipe, for the observation state's label (ADR-0134). */
  vocab?: ObservationPhaseVocabulary | undefined;
  /** ADR-0169 — the response's `photoReview` plus its report. Cards only;
   *  absent or `enabled: false` renders exactly as before. */
  review?: PhotoReviewContext | undefined;
  variant?: "strip" | "hero";
  emptyText?: string;
};

const DEFAULT_EMPTY = "Este reporte no tiene fotos registradas.";

// `variant="hero"`: large protagonist tiles for the store-detail carousel
// (permanent per founder decision 2026-10-22). Same badge rule as the strip.
export function PhotoGallery({ groups, vocab, review, ...rest }: PhotoGalleryProps) {
  if (groups && groups.length > 0 && rest.photos.length > 0) {
    return (
      <ObservationCards
        photos={rest.photos}
        groups={groups}
        vocab={vocab}
        review={review?.enabled ? review : undefined}
      />
    );
  }
  return <PhotoStrip {...rest} />;
}

function PhotoStrip({
  photos,
  variant = "strip",
  emptyText = DEFAULT_EMPTY,
}: Omit<PhotoGalleryProps, "groups" | "vocab" | "review">) {
  const hero = variant === "hero";
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  // The lightbox has one area line; carry the checklist item there too.
  const lightboxPhotos = useMemo(
    () =>
      photos.map((p) => ({
        ...p,
        areaTag: p.itemLabel ? [p.areaTag, p.itemLabel].filter(Boolean).join(" · ") : p.areaTag,
      })),
    [photos]
  );

  if (photos.length === 0) {
    return (
      <div className="vgal__empty">
        <Icon.Photo s={16} />
        <span>{emptyText}</span>
      </div>
    );
  }

  return (
    <>
      <div className={hero ? "vgal__strip vgal__strip--hero" : "vgal__strip"}>
        {photos.map((photo, i) => {
          const flag = photoFlag(photo);
          return (
            <button
              key={photo.id}
              type="button"
              className="vgal__tile"
              onClick={() => setOpenIndex(i)}
            >
              <span className="vgal__shot">
                {/* alt="" — this button's accessible name is already
                    carried by the visible area-tag/item/caption text below
                    it (or the sr-only fallback when all are absent); a
                    substantive alt here would double-announce. */}
                <img
                  src={photo.url}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  width={hero ? 480 : 176}
                  height={hero ? 340 : 124}
                />
                <span className={`vgal__flag vgal__flag--${flag.tone}`}>{flag.label}</span>
              </span>
              {photo.areaTag && <span className="vgal__area-tag">{photo.areaTag}</span>}
              {photo.itemLabel && <span className="vgal__item">{photo.itemLabel}</span>}
              {photo.caption && <span className="vgal__cap">{photo.caption}</span>}
              {!photo.areaTag && !photo.itemLabel && !photo.caption && (
                <span className="sr-only">Ampliar foto</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Lightbox.tsx owns the <dialog>; PhotoGallery keeps the index. */}
      <Lightbox
        photos={lightboxPhotos}
        openIndex={openIndex}
        onClose={() => setOpenIndex(null)}
        onNavigate={setOpenIndex}
      />
    </>
  );
}

// ── One card per observation (ADR-0137 Postscript) ─────────────────────
// The item's verdict, label and caption travel once per card; the photos
// fill the hero block as a mosaic (1 full, 2 side by side, 3 = one tall +
// two stacked). There is no per-photo verdict: in a multi-photo problem
// card every photo says «Sin evaluar», because only one of them may show
// the defect and a photo must never read as conforming by the absence of
// a mark (founder rule).
//
// ADR-0169 adds a per-photo REVIEW state on top, when the server sends
// one (`photo.review`): the AI proposal or the supervisor's confirmed
// decision, badge per photo. The card pill still reads the signed item;
// the decision controls live only in the lightbox footer, never inside a
// mosaic cell (a button in a button).

// A monochrome glyph per state so the pill doesn't rely on colour alone
// (the svg is aria-hidden; the text carries the state).
const STATE_ICON: Partial<Record<PresentationalKind, typeof Icon.Check>> = {
  conforme: Icon.Check,
  assigned: Icon.Clock,
  resolved: Icon.Eye,
  rejected: Icon.Close,
  reopened: Icon.Warning,
};

function GroupPillView({ pill }: { pill: GroupPill }) {
  const StateIcon = pill.kind ? STATE_ICON[pill.kind] : undefined;
  return (
    <span className={`vgal__flag vgal__flag--static vgal__flag--${pill.tone}`}>
      {StateIcon && <StateIcon s={12} />}
      {pill.verdictLabel}
      {pill.verdictLabel && pill.estado && (
        <>
          <span aria-hidden="true"> · </span>
          <span className="sr-only">, </span>
        </>
      )}
      {pill.estado}
    </span>
  );
}

type Card = {
  group: ReportPhotoGroup;
  photos: ReportPhoto[];
  title: string;
  pill: GroupPill;
  perPhotoUnrated: boolean;
  /** Per displayed photo; null = no review state for that photo. */
  badges: (ReviewBadgeModel | null)[];
  /** Some photo carries its own review state (the card-level «el estado
   *  aplica a la observación» note no longer holds). */
  reviewed: boolean;
};

// A confirmed problem reads with the item's severity; an item verdict
// that is not a problem (none / ok) never paints a defect green.
function defectToneOf(group: ReportPhotoGroup): FlagTone {
  const t = verdictFlag(group).tone;
  return t === "ok" ? "none" : t;
}

function ObservationCards({
  photos,
  groups,
  vocab,
  review,
}: {
  photos: ReportPhoto[];
  groups: readonly ReportPhotoGroup[];
  vocab: ObservationPhaseVocabulary | undefined;
  review: PhotoReviewContext | undefined;
}) {
  const idBase = useId();
  const cards = useMemo<Card[]>(() => {
    const byId = new Map(photos.map((p) => [p.id, p]));
    return groups
      .map((group) => {
        const groupPhotos = group.photoIds
          .map((id) => byId.get(id))
          .filter((p): p is ReportPhoto => p !== undefined);
        const unattributed = isUnattributedGroup(group);
        const defectTone = defectToneOf(group);
        // An item without observation (e.g. conforming) or a single-photo
        // item is not under review: no per-photo badge, its pill covers
        // it (groupUnderPhotoReview).
        const underReview = !unattributed && groupUnderPhotoReview(group);
        const badges = groupPhotos.map((p) =>
          review && underReview && p.review ? reviewBadge(p.review, defectTone) : null
        );
        return {
          group,
          photos: groupPhotos,
          title: groupTitle(group),
          pill: unattributed
            ? ({ tone: "none", verdictLabel: "Sin evaluar", estado: null, kind: null } as const)
            : groupPill(group, vocab),
          perPhotoUnrated: !unattributed && groupPhotos.length > 1 && isProblemGroup(group),
          badges,
          reviewed: badges.some((b) => b !== null),
        };
      })
      .filter((c) => c.photos.length > 0);
  }, [photos, groups, vocab, review]);

  // Lightbox scope = one card: ← → wrap inside that observation only.
  // Addressed by identity, never by position: an annulment (or any
  // refetch) re-sorts the cards — an annulled observation moves to the
  // closed band — and a card index would then point at another
  // observation's photos.
  const [open, setOpen] = useState<{ groupId: string; photoId: string } | null>(null);
  const cellRefs = useRef(new Map<string, HTMLButtonElement>());
  const cardRefs = useRef(new Map<string, HTMLLIElement>());
  // The photo on screen when the lightbox closes gets the focus back —
  // not the cell that opened it — so a keyboard user lands where they
  // left off. Falls back to the card if that cell is gone.
  const lastViewed = useRef<{ groupId: string; photoId: string } | null>(null);
  const wasOpen = useRef(false);
  // ADR-0169 review state of the open lightbox.
  const [announce, setAnnounce] = useState("");
  const [alertText, setAlertText] = useState("");
  const [annulRequest, setAnnulRequest] = useState<AnnulRequest | null>(null);
  const [justAnnulled, setJustAnnulled] = useState<string | null>(null);
  const clearJustAnnulled = useCallback(() => setJustAnnulled(null), []);
  const [focusDecision, setFocusDecision] = useState<string | null>(null);
  const clearFocusDecision = useCallback(() => setFocusDecision(null), []);
  // A refusal that must outlive the decision controls (ReviewNotice).
  const [notice, setNotice] = useState<{ groupId: string; text: string; focus: boolean } | null>(
    null
  );
  const clearNoticeFocus = useCallback(
    () => setNotice((n) => (n && n.focus ? { ...n, focus: false } : n)),
    []
  );

  function show(card: number, index: number) {
    const c = cards[card];
    const photo = c?.photos[index];
    if (!c || !photo) return;
    lastViewed.current = { groupId: c.group.id, photoId: photo.id };
    if (!open || open.groupId !== c.group.id) {
      setAnnounce("");
      setAlertText("");
      setNotice(null);
    }
    setOpen({ groupId: c.group.id, photoId: photo.id });
  }

  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    const lv = lastViewed.current;
    if (!lv) return;
    (cellRefs.current.get(lv.photoId) ?? cardRefs.current.get(lv.groupId))?.focus();
  }, [open]);

  const openCardIndex = open ? cards.findIndex((c) => c.group.id === open.groupId) : -1;
  const openCard = openCardIndex >= 0 ? cards[openCardIndex] : undefined;
  const openIndex = open && openCard ? openCard.photos.findIndex((p) => p.id === open.photoId) : -1;

  // The open observation or photo really went away (not a re-sort):
  // close, and the effect above returns focus to where the user was.
  useEffect(() => {
    if (open && openIndex < 0) setOpen(null);
  }, [open, openIndex]);
  // Rebuilt each render: the review footer closes over this component's
  // state, and the Lightbox needs no referential stability.
  const lightboxPhotos: LightboxPhoto[] = (() => {
    if (!openCard) return [];
    const { group, pill } = openCard;
    const area =
      group.itemLabel && group.areaTag ? `${group.areaTag} · ${group.itemLabel}` : openCard.title;
    return openCard.photos.map((p, i) => {
      const badge = openCard.badges[i];
      return {
        url: p.url,
        caption: group.caption ?? p.caption,
        areaTag: area,
        badge: badge ? (
          <>
            <GroupPillView pill={pill} />
            <ReviewBadge badge={badge} inCell={false} />
          </>
        ) : (
          <GroupPillView pill={pill} />
        ),
        footer: reviewFooter(openCard, p, i),
      };
    });
  })();

  function reviewFooter(card: Card, photo: ReportPhoto, index: number): ReactNode {
    const { group } = card;
    if (!review || isUnattributedGroup(group)) return undefined;
    const noticeLine =
      notice && notice.groupId === group.id ? (
        <ReviewNotice text={notice.text} focusOnMount={notice.focus} onFocused={clearNoticeFocus} />
      ) : null;
    // Annulment before reviewable: the server never sends an annulled
    // group as reviewable, and every viewer reads who annulled it.
    if (group.annulment) {
      return (
        <>
          {noticeLine}
          <AnnulledLine
            group={group}
            focusOnMount={justAnnulled === group.id && notice === null}
            onFocused={clearJustAnnulled}
          />
        </>
      );
    }
    if (
      !group.reviewable ||
      !groupUnderPhotoReview(group) ||
      !review.canReview ||
      photo.review === null
    ) {
      return noticeLine ?? undefined;
    }
    const n = card.photos.length;
    let next: number | null = null;
    for (let step = 1; step < n; step++) {
      const j = (index + step) % n;
      if (isPendingReview(card.photos[j]!)) {
        next = j;
        break;
      }
    }
    const cardIndex = cards.indexOf(card);
    return (
      <>
        {noticeLine}
        <p className="vlb__note">
          El acta ya está firmada y no cambia. Tu revisión se guarda aparte, como complemento.
        </p>
        <PhotoReviewControls
          reportId={review.reportId}
          group={group}
          photos={card.photos}
          photo={photo}
          itemLabel={card.title}
          focusLabel={focusDecision === photo.id}
          onFocusLabelDone={clearFocusDecision}
          onRequestAnnul={setAnnulRequest}
          onRefused={(text) => {
            setNotice({ groupId: group.id, text, focus: true });
            setAlertText(text);
          }}
          onDecided={(verdict, annulled) => {
            setNotice(null);
            if (annulled) {
              setJustAnnulled(group.id);
              setAlertText("La observación quedó anulada.");
              return;
            }
            const left = card.photos.filter((p) => p.id !== photo.id && isPendingReview(p)).length;
            setAnnounce(
              `Foto ${index + 1} de ${n}: ${DECISION_TEXT[verdict]}. ${remainingText(left)}`
            );
          }}
        />
        {next !== null && (
          <button
            type="button"
            className="vlb__btn vlb__btn--secondary vlb__next"
            onClick={() => {
              const target = card.photos[next]!;
              setFocusDecision(target.id);
              show(cardIndex, next);
            }}
          >
            Siguiente por revisar
          </button>
        )}
      </>
    );
  }

  return (
    <>
      <ul
        className="vgal__strip vgal__strip--hero vgal__cards"
        aria-label="Fotos del último reporte"
      >
        {cards.map((c, ci) => {
          const titleId = `${idBase}-${ci}`;
          const n = c.photos.length;
          const unattributed = isUnattributedGroup(c.group);
          return (
            <li
              key={c.group.id}
              className="vgal__card"
              aria-labelledby={titleId}
              tabIndex={-1}
              ref={(el) => {
                if (el) cardRefs.current.set(c.group.id, el);
                else cardRefs.current.delete(c.group.id);
              }}
            >
              <div className={`vgal__shot vgal__mosaic vgal__mosaic--n${Math.min(n, 3)}`}>
                {c.photos.map((p, i) => {
                  const badge = c.badges[i];
                  const estado = badge
                    ? `. ${reviewBadgeText(badge)}`
                    : c.perPhotoUnrated && !c.reviewed
                      ? ". Sin evaluar"
                      : "";
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className="vgal__cell"
                      aria-label={`Ampliar foto ${i + 1} de ${n}${estado}`}
                      onClick={() => show(ci, i)}
                      ref={(el) => {
                        if (el) cellRefs.current.set(p.id, el);
                        else cellRefs.current.delete(p.id);
                      }}
                    >
                      <img src={p.url} alt="" loading="lazy" decoding="async" />
                      {badge ? (
                        <ReviewBadge badge={badge} inCell />
                      ) : (
                        c.perPhotoUnrated &&
                        !c.reviewed && (
                          <span className="vgal__flag vgal__flag--none" aria-hidden="true">
                            Sin evaluar
                          </span>
                        )
                      )}
                    </button>
                  );
                })}
              </div>
              {!unattributed && c.group.areaTag && (
                <span className="vgal__area-tag">{c.group.areaTag}</span>
              )}
              <span className="vgal__item" id={titleId}>
                {c.title}
              </span>
              {c.group.caption && (
                <span className="vgal__cap vgal__cap--clamp">{c.group.caption}</span>
              )}
              <GroupPillView pill={c.pill} />
              {c.perPhotoUnrated && !c.reviewed && (
                <p className="vgal__note">El estado aplica a la observación, no a cada foto.</p>
              )}
              {review && <CardReviewLines card={c} review={review} onReview={(i) => show(ci, i)} />}
              {unattributed && (
                <p className="vgal__note">Esta visita no registró el detalle por foto.</p>
              )}
              {c.group.totalPhotos > n && (
                <p className="vgal__note">
                  Se muestran {n} de {c.group.totalPhotos} fotos
                </p>
              )}
              {review && c.group.reviewable && c.group.photoSetSize > n && (
                <p className="vgal__note">
                  Las observaciones con más de 3 fotos no se anulan automáticamente.
                </p>
              )}
            </li>
          );
        })}
      </ul>

      <Lightbox
        photos={lightboxPhotos}
        openIndex={openIndex >= 0 ? openIndex : null}
        onClose={() => setOpen(null)}
        onNavigate={(i) => (openCardIndex >= 0 ? show(openCardIndex, i) : undefined)}
        ariaLabel={
          openCard ? `Fotos de la observación: ${openCard.title}` : "Fotos de la observación"
        }
        countLabel={openCard?.title}
        status={review ? announce : undefined}
        alert={review ? alertText : undefined}
      />
      {review && (
        <ConfirmAnnulDialog request={annulRequest} onClose={() => setAnnulRequest(null)} />
      )}
    </>
  );
}

// The card's review summary (ADR-0169): what is left to decide, or who
// annulled it. Read-only viewers only learn that a supervisor has work.
function CardReviewLines({
  card,
  review,
  onReview,
}: {
  card: Card;
  review: PhotoReviewContext;
  onReview: (index: number) => void;
}) {
  const { group, photos } = card;
  if (isUnattributedGroup(group)) return null;
  if (group.annulment) return <AnnulledLine group={group} />;
  if (!group.reviewable || !groupUnderPhotoReview(group) || !card.reviewed) return null;
  const pendingIdx = photos.findIndex(isPendingReview);
  if (pendingIdx < 0) return null;
  if (!review.canReview) {
    return photos.some((p) => p.review?.phase === "proposed") ? (
      <p className="vgal__note">Hay fotos por revisar por un supervisor.</p>
    ) : null;
  }
  const k = photos.filter(isPendingReview).length;
  return (
    <div className="vgal__review">
      <p className="vgal__note">
        {k} de {photos.length} fotos por revisar
      </p>
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        onClick={() => onReview(pendingIdx)}
      >
        Revisar fotos
      </button>
    </div>
  );
}
